import { urlIcone3d } from '../utils/iconesVeiculos'
import React, { useState, useEffect, useMemo } from 'react'
import axios from 'axios'

/**
 * SeletorIconeAtivo
 *
 * Modal de seleção de ícone + cor para um ativo. Busca o catálogo
 * direto da API (GET /api/icones-veiculos/catalogo) — não duplica a
 * lista de ícones aqui no frontend, então adicionar um ícone novo no
 * backend não exige nenhuma alteração neste arquivo.
 *
 * Uso (dentro de Ativos.tsx):
 *   import SeletorIconeAtivo from '../components/SeletorIconeAtivo'
 *   ...
 *   const [seletorIconeAberto, setSeletorIconeAberto] = useState(false)
 *   ...
 *   {seletorIconeAberto && (
 *     <SeletorIconeAtivo
 *       iconIdAtual={form.icon_id}
 *       corAtual={form.icon_color}
 *       onConfirmar={({ iconId, cor }) => {
 *         setForm({ ...form, icon_id: iconId, icon_color: cor })
 *         setSeletorIconeAberto(false)
 *       }}
 *       onFechar={() => setSeletorIconeAberto(false)}
 *     />
 *   )}
 */

const API = process.env.REACT_APP_API_URL || '/api'

const PALETA_CORES = [
  { id: 'cinza', label: 'Cinza', hex: '#94a3b8' },
  { id: 'branco', label: 'Branco', hex: '#f1f5f9' },
  { id: 'preto', label: 'Preto', hex: '#1e293b' },
  { id: 'vermelho', label: 'Vermelho', hex: '#ef4444' },
  { id: 'azul', label: 'Azul', hex: '#3b82f6' },
  { id: 'verde', label: 'Verde', hex: '#22c55e' },
  { id: 'amarelo', label: 'Amarelo', hex: '#eab308' },
  { id: 'laranja', label: 'Laranja', hex: '#f97316' },
]

// Filtros CSS calculados via otimizacao numerica (matrizes reais de
// grayscale/sepia/saturate/hue-rotate/brightness do navegador) para
// reproduzir com precisao cada cor da paleta sobre a base cinza neutra
// dos icones topdown. Mesma tabela usada em Mapa.tsx. Erro medido: 0.0.
const FILTROS_COR_EXATOS: Record<string, string> = {
  '#94a3b8': 'grayscale(1) sepia(1) saturate(0.64) hue-rotate(175.6deg) brightness(0.90)',
  '#f1f5f9': 'grayscale(1) sepia(1) saturate(0.09) hue-rotate(169deg) brightness(1.37)',
  '#1e293b': 'grayscale(1) sepia(1) saturate(3.67) hue-rotate(188.9deg) brightness(0.23)',
  '#ef4444': 'grayscale(1) sepia(1) saturate(47.92) hue-rotate(333.6deg) brightness(0.94)',
  '#3b82f6': 'grayscale(1) sepia(1) saturate(39.44) hue-rotate(200.8deg) brightness(0.96)',
  '#22c55e': 'grayscale(1) sepia(1) saturate(3.43) hue-rotate(89.2deg) brightness(0.87)',
  '#eab308': 'grayscale(1) sepia(1) saturate(3.96) hue-rotate(7.4deg) brightness(1.00)',
  '#f97316': 'grayscale(1) sepia(1) saturate(28.18) hue-rotate(349.4deg) brightness(0.98)',
}
function filtroCor(hex?: string | null): string {
  if (!hex) return 'none'
  return FILTROS_COR_EXATOS[hex.toLowerCase()] || 'none'
}

function urlIcone(iconId: string): string {
  // Em produção, os ícones são servidos pelo próprio domínio em /vehicle-icons/
  return `/vehicle-icons/${iconId}.png`
}

interface IconeCategoria {
  id: string
  label: string
  icons: { id: string; label: string; recolorable?: boolean; rotationStyle?: string }[]
}

interface Props {
  iconIdAtual?: string | null
  corAtual?: string | null
  onConfirmar: (resultado: { iconId: string | null; cor: string | null }) => void
  onFechar: () => void
}

export default function SeletorIconeAtivo({ iconIdAtual, corAtual, onConfirmar, onFechar }: Props) {
  const [categorias, setCategorias] = useState<IconeCategoria[]>([])
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState('')
  const [categoriaAtiva, setCategoriaAtiva] = useState('')
  const [iconePendente, setIconePendente] = useState<string | null>(iconIdAtual || null)
  const [corPendente, setCorPendente] = useState<string>(corAtual || '#94a3b8')

  useEffect(() => {
    axios.get(`${API}/icones-veiculos/catalogo`)
      .then(r => {
        const cats = r.data.categorias || []
        setCategorias(cats)
        if (cats.length > 0) setCategoriaAtiva(cats[0].id)
      })
      .catch(() => setErro('Não foi possível carregar o catálogo de ícones.'))
      .finally(() => setCarregando(false))
  }, [])

  const categoria = useMemo(
    () => categorias.find(c => c.id === categoriaAtiva),
    [categorias, categoriaAtiva]
  )

  const filter = useMemo(() => filtroCor(corPendente), [corPendente])

  const iconePendenteObj = useMemo(
    () => categorias.flatMap(c => c.icons).find(i => i.id === iconePendente),
    [categorias, iconePendente]
  )
  const podeRecolorir = iconePendenteObj?.recolorable !== false

  return (
    <div className="modal-overlay" onClick={onFechar}>
      <div className="modal" style={{ width: 640, maxHeight: '85vh', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>🎨 Escolher ícone do ativo</h3>
          <button className="btn btn-ghost" style={{ padding: '4px 8px' }} onClick={onFechar}>✕</button>
        </div>

        {carregando && (
          <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Carregando catálogo...</div>
        )}

        {erro && (
          <div style={{ background: '#450a0a', border: '1px solid #dc2626', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 12, color: '#f87171' }}>
            ⚠️ {erro}
          </div>
        )}

        {!carregando && !erro && (
          <>
            {/* Tabs de categoria */}
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', paddingBottom: 12, marginBottom: 12, borderBottom: '1px solid #2d4166', overflowX: 'auto' }}>
              {categorias.map(cat => {
                const ativa = cat.id === categoriaAtiva
                return (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => setCategoriaAtiva(cat.id)}
                    style={{
                      padding: '6px 14px',
                      borderRadius: 999,
                      border: 'none',
                      background: ativa ? '#38bdf8' : '#0c2a4a',
                      color: ativa ? '#06283f' : '#94a3b8',
                      fontSize: 12.5,
                      fontWeight: 600,
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {cat.label}
                  </button>
                )
              })}
            </div>

            {/* Grid de ícones */}
            <div style={{ overflowY: 'auto', flex: 1, marginBottom: 16 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(88px, 1fr))', gap: 10 }}>
                {categoria?.icons.map(icon => {
                  const selecionado = icon.id === iconePendente
                  return (
                    <button
                      key={icon.id}
                      type="button"
                      onClick={() => setIconePendente(icon.id)}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 6,
                        padding: '10px 6px',
                        borderRadius: 10,
                        border: selecionado ? '2px solid #38bdf8' : '1px solid #2d4166',
                        background: selecionado ? '#0c2a4a' : 'transparent',
                        cursor: 'pointer',
                      }}
                    >
                      <div style={{ width: 64, height: 64, filter: (icon.recolorable === false || icon.id.startsWith('v3d_')) ? 'none' : filter }}>
                        <img src={icon.id.startsWith('v3d_') ? urlIcone3d(icon.id, corPendente, 225) : urlIcone(icon.id)} alt={icon.label} width={64} height={64} style={{ objectFit: 'contain' }} />
                      </div>
                      <span style={{ fontSize: 10.5, fontWeight: 500, color: '#cbd5e1', textAlign: 'center', lineHeight: 1.2 }}>
                        {icon.label}
                      </span>
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Seletor de cor (somente para icones recoloraveis) */}
            <div style={{ borderTop: '1px solid #2d4166', paddingTop: 14 }}>
              {podeRecolorir && (
                <>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 8 }}>
                    Cor do veículo
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
                {PALETA_CORES.map(c => {
                  const ativa = corPendente === c.hex
                  return (
                    <button
                      key={c.id}
                      type="button"
                      title={c.label}
                      onClick={() => setCorPendente(c.hex)}
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: 8,
                        border: ativa ? '2px solid #38bdf8' : '1px solid #2d4166',
                        background: c.hex,
                        cursor: 'pointer',
                      }}
                    />
                  )
                })}
                  </div>
                </>
              )}

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-ghost" onClick={onFechar}>Cancelar</button>
                <button
                  type="button"
                  disabled={!iconePendente}
                  onClick={() => onConfirmar({ iconId: iconePendente, cor: corPendente })}
                  style={{
                    padding: '9px 18px',
                    borderRadius: 8,
                    border: 'none',
                    background: iconePendente ? '#38bdf8' : '#2d4166',
                    color: iconePendente ? '#06283f' : '#64748b',
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: iconePendente ? 'pointer' : 'not-allowed',
                  }}
                >
                  Confirmar
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
